import React, { type ReactNode, useEffect, useState } from 'react';

import { Text } from '@atlaskit/primitives/compiled';

import { type LazyMessages } from './types';

export const DEFAULT_LOCALE = 'en';
const localeRegex = /([a-z]+)(?:[-_]([A-Z]+))?/i;

export const DEFAULT_RICH_TEXT_ELEMENTS: {
	b: (chunks: ReactNode[]) => React.JSX.Element;
	strong: (chunks: ReactNode[]) => React.JSX.Element;
} = {
	b: (chunks: ReactNode[]) => <Text weight="bold">{chunks}</Text>,
	strong: (chunks: ReactNode[]) => <Text as="strong">{chunks}</Text>,
};

export const getCodesFromLocale: (locale: string) =>
	| {
			language: string;
			country?: string;
	  }
	| undefined = (locale: string): { language: string; country?: string } | undefined => {
	const match = localeRegex.exec(locale);
	if (match) {
		const [, language, country] = match;
		return {
			language: language.toLowerCase(),
			country: country?.toUpperCase(),
		};
	}
	// TODO: Use error service to log:
	// eslint-disable-next-line no-console
	console.error('Unable to determine language and country from invalid locale.');
	return undefined;
};

const loadLazyMessages = async (
	lazyMessages: LazyMessages,
	language: string,
	country?: string,
): Promise<Record<string, string>> =>
	(
		await (
			(country && lazyMessages[`${language}_${country}`]) ||
			lazyMessages[language] ||
			lazyMessages.en
		)?.()
	)?.default ?? {};

export const useLazyMessages: (
	lazyMessages: LazyMessages,
	language: string,
	country?: string,
) => Record<string, string> | undefined = (
	lazyMessages: LazyMessages,
	language: string,
	country?: string,
): Record<string, string> | undefined => {
	const [messages, setMessages] = useState<Record<string, string>>();

	useEffect(() => {
		loadLazyMessages(lazyMessages, language, country)
			.catch(() => ({}))
			.then(setMessages);
	}, [country, language, lazyMessages, setMessages]);

	return messages;
};
